import type { TFunction } from 'i18next';
import type { CommandId } from '@os/engine';
import type { ExportFormat } from '../export/ExportDialog';
import type { PaletteCommand } from '../palette/commands';
import { shortcutOf } from './palette-commands';

export type MainMenuLeaf = {
  id: string;
  label: string;
  shortcut?: string;
  disabled?: boolean;
  /** A toggle (shown checked or not). */
  checked?: boolean;
  run: () => void;
};
export type MainMenuGroup = { id: string; label: string; children: MainMenuEntry[] };
export type MainMenuEntry = MainMenuLeaf | MainMenuGroup | { separator: true };

export type MainMenuContext = {
  t: TFunction;
  readOnly: boolean;
  /** The editor's commands (`controller.runCommand`). */
  run: (id: CommandId) => void;
  /** What the rest of the page registered in the ⌘K palette (version history…), reused by id. */
  registered: readonly PaletteCommand[];
  onRename?: () => void;
  onDuplicateBoard?: () => void;
  /** Absent without `can_export`. */
  onExport?: (format: ExportFormat) => void;
  /** File › Import › Board file (P9). */
  onImport?: () => void;
  /** File › Import › From Figma (P11b): connected boards, editors. */
  onImportFigma?: () => void;
  /** File › Import › CSV (FigJam) (P11b). */
  onImportCsv?: () => void;
  onSaveTemplate?: () => void;
  /** Board settings (grid, background). */
  onSettings: () => void;
  /** Snap to grid, this person's toggle (P16). */
  snap?: { on: boolean; toggle: () => void };
  onToggleMinimap?: () => void;
  onTheme?: () => void;
  onHelp: () => void;
  /** Help › About (P5g): the web and Worker builds, copied for a bug report. */
  onAbout?: () => void;
  /** The board's sections in reading order (read when the menu opens), and going to one (P5e). */
  sections?: readonly { id: string; name: string }[];
  onGoToSection?: (id: string) => void;
  /** Object › Task list (P16-5): a new list at the view's centre. */
  onInsertTaskList?: () => void;
  /** « Lien vers une page » (P14): editors. */
  onInsertPageLink?: () => void;
  /** Phones (P5f, backlog §14.4): share, history and comments leave the top bar for a Board submenu, first. */
  phone?: boolean;
};

const EXPORTS: readonly ExportFormat[] = ['png', 'jpeg', 'pdf', 'json', 'osboard'];
const EDITING = new Set<string>([
  'undo',
  'redo',
  'cut',
  'paste',
  'duplicate',
  'delete',
  'importFile',
  'importFigma',
  'importCsv',
  'rename',
  'saveTemplate',
]);
const sep = { separator: true } as const;

/**
 * The board's main menu (P5d, backlog §10.1): submenus File, Edit, View, Object, Text, Arrange, then Preferences, then
 * Help. Pure: the UI renders it. Keymap commands show the palette's shortcut; a read-only board disables what edits.
 */
export function mainMenu(ctx: MainMenuContext): MainMenuEntry[] {
  const { t, readOnly } = ctx;
  const leaf = (
    id: string,
    label: string,
    run: () => void,
    editing = EDITING.has(id),
  ): MainMenuLeaf => ({
    id,
    label,
    run,
    shortcut: shortcutOf(id as CommandId),
    disabled: readOnly && editing,
  });
  const cmd = (id: CommandId, editing = EDITING.has(id)) =>
    leaf(id, t(`commands.${id}`), () => ctx.run(id), editing);
  const registered = (id: string) => {
    const c = ctx.registered.find((r) => r.id === id);
    return c ? [leaf(id, c.label, c.run, false)] : [];
  };
  const opt = (id: string, run: (() => void) | undefined) =>
    run ? [leaf(id, t(`mainMenu.${id}`), run)] : [];
  const sub = (id: string, children: MainMenuEntry[]): MainMenuGroup => ({
    id,
    label: t(`mainMenu.${id}`),
    children: tidy(children),
  });
  const exporter = ctx.onExport;
  return tidy([
    // Phones (P5f, P16-3): the bar keeps the avatars (members, Share) and the comments; Board holds the history only,
    // nothing twice (backlog §19 P16-3.2).
    ...(ctx.phone ? [sub('board', [...registered('pages'), ...registered('history')]), sep] : []),
    sub('file', [
      // The way back (P5f): the page registers it (its project and the dashboard, or all boards for a local board).
      ...registered('back'),
      ...registered('dashboard'),
      sep,
      ...opt('rename', ctx.onRename),
      ...opt('duplicateBoard', ctx.onDuplicateBoard),
      sep,
      ...(exporter
        ? EXPORTS.map((f) =>
            leaf(
              `export:${f}`,
              t('mainMenu.exportAs', { format: t(`mainMenu.formats.${f}`) }),
              () => exporter(f),
            ),
          )
        : []),
      sub('import', [...opt('importFile', ctx.onImport), ...opt('importFigma', ctx.onImportFigma), ...opt('importCsv', ctx.onImportCsv)]),
      sep,
      ...opt('saveTemplate', ctx.onSaveTemplate),
      // Share left the top bar (P16-3): File keeps it, with the members panel and Cmd/Ctrl+Shift+S.
      ...(ctx.phone ? [] : registered('share')),
      ...(ctx.phone ? [] : registered('history')),
    ]),
    sub('edit', [
      cmd('undo'),
      cmd('redo'),
      sep,
      cmd('cut'),
      cmd('copy'),
      cmd('paste'),
      cmd('duplicate'),
      cmd('delete'),
      sep,
      cmd('selectAll', false),
    ]),
    sub('view', [
      ...(ctx.snap ? [{ ...leaf('snapToGrid', t('mainMenu.snapToGrid'), ctx.snap.toggle, false), checked: ctx.snap.on }] : []),
      leaf('grid', t('mainMenu.grid'), ctx.onSettings, false),
      sep,
      cmd('zoomIn'),
      cmd('zoomOut'),
      cmd('zoomFit'),
      cmd('zoomReset'),
      cmd('zoomSelection'),
      ...(ctx.onGoToSection
        ? [
            sub(
              'sections',
              ctx.sections?.length
                ? ctx.sections.map((s) => leaf(`goToSection:${s.id}`, s.name || t('editor.sectionPlaceholder'), () => ctx.onGoToSection!(s.id), false))
                : [{ ...leaf('noSections', t('mainMenu.noSections'), () => undefined, false), disabled: true }],
            ),
          ]
        : []),
      sep,
      ...opt('minimap', ctx.onToggleMinimap),
      ...opt('theme', ctx.onTheme),
    ]),
    sub('object', [
      ...(ctx.onInsertTaskList ? [leaf('insertTaskList', t('mainMenu.insertTaskList'), ctx.onInsertTaskList, true)] : []),
      ...(ctx.onInsertPageLink ? [leaf('insertPageLink', t('mainMenu.insertPageLink'), ctx.onInsertPageLink, true)] : []),
      ...(ctx.onInsertTaskList || ctx.onInsertPageLink ? [sep] : []),
      cmd('group', true),
      cmd('ungroup', true),
      cmd('toggleLock', true),
      sep,
      cmd('bringToFront', true),
      cmd('bringForward', true),
      cmd('sendBackward', true),
      cmd('sendToBack', true),
    ]),
    sub('text', [cmd('copyStyle', false), cmd('pasteStyle', true)]),
    sub('arrange', [
      cmd('alignLeft', true),
      cmd('alignHCenter', true),
      cmd('alignRight', true),
      cmd('alignTop', true),
      cmd('alignVCenter', true),
      cmd('alignBottom', true),
      sep,
      cmd('distributeH', true),
      cmd('distributeV', true),
      cmd('arrange', true),
      cmd('fitToContent', true),
    ]),
    sep,
    sub('preferences', [
      leaf('settings', t('mainMenu.settings'), ctx.onSettings, false),
      ...opt('theme', ctx.onTheme),
    ]),
    // The account left the top bar (P16-3): members only, the page registers its entries (a guest has none).
    ...(ctx.registered.some((r) => r.id === 'signOut')
      ? [sub('account', [...registered('profile'), ...registered('language'), ...opt('theme', ctx.onTheme), sep, ...registered('signOut')])]
      : []),
    sep,
    sub('help', [leaf('shortcuts', t('mainMenu.shortcuts'), ctx.onHelp, false), ...opt('about', ctx.onAbout)]),
  ]);
}

/** No empty submenu, and no leading, trailing or doubled separators. */
function tidy(entries: MainMenuEntry[]): MainMenuEntry[] {
  const out: MainMenuEntry[] = [];
  for (const e of entries) {
    if ('children' in e && !e.children.length) continue;
    if ('separator' in e && (!out.length || 'separator' in out.at(-1)!)) continue;
    out.push(e);
  }
  while (out.length && 'separator' in out.at(-1)!) out.pop();
  return out;
}
